import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import ActionTypes from "actions/action-types";
import ActionLogger from "action-logger";
import AnalyticsPreviousLocationContextActions from "actions/analytics-previous-location-context-actions";
import RatingEducationTooltipsActions from "actions/rating-education-tooltips-actions";
import { getDueDate, inMaternityExperience } from "helpers/maternity-helper";
import MembershipHelpers from "helpers/membership-helpers";
import MembershipUpgradesHelpers from "helpers/membership-upgrades-helpers";
import PageViewActions from "actions/logging/page-view";
import PDPContent from "components/source/products/pdp-content";
import ProductAvailability from "helpers/product-availability";
import ProductsActions from "actions/products-actions";
import smartComponentActions from "actions/smart-component-actions";
import PropTypes from "prop-types";
import RentalOptionIntent from "helpers/rental-option-intent-helper";
import ReservationsActions from "actions/reservations-actions";
import SharedActions from "actions/shared-actions";
import WindowActions from "actions/window-actions";
import productRatingsActions from "actions/product-ratings-actions";
import shortlistsActions from "actions/shortlists-actions";
import { isStorefrontNext } from "helpers/environment-helpers";
import GTMHelper from "helpers/gtm";
import HeapHelpers from "helpers/heap-helpers";
import { buildPDPEvents, buildPDPDrawerEvents } from "helpers/heap-pdp-helpers";
import { getSelectedSku, getSkuCount, getAvailableSku } from "helpers/sku-helpers";
import {
  getUserId,
  withUserData,
  userDataPropType,
  isAnonymous,
  isIdentified,
  isAuthorized,
} from "components/source/hoc/with-user-data";
import { displayMembershipUpsell, logMembershipUpsellEligibility } from "helpers/membership-upsell-helper";
import { inventoryEligibilities, membershipLens, partialPropsStateKeys } from "rtr-constants";
import {
  browserPropType,
  gridAndCarouselContextPropType,
  membershipBagPropType,
  membershipStatePropType,
  ordersPropType,
  productPropType,
  reservationPropType,
  PDPContentPropType,
} from "components/propTypes";
import { withFeatureFlags, featureFlagsPropType, Flags } from "components/source/hoc/with-feature-flags";
import { withBrowser } from "../../layout/BrowserContext";
import {
  isAccessoryProductCategory,
  isBackupSizeCategory,
  isRentableProductCategory,
} from "helpers/product-category-helpers";
import { isBulk, isClearance } from "helpers/price-helper";
import Converge from "helpers/converge";
import { logReservationAction } from "helpers/reservation-pixel-helper";
import { loadAddressBook } from "actions/shipping-step-actions";
import { buildPdpCanonicalUrl } from "helpers/third-party/sailthru-helpers";
import UpsellBonusStyle from "components/source/reservations/upsell-bonus-style/upsell-bonus-style";
import {
  flagsAndExperimentNames,
  withOnLoadFlagsAndExperiments,
} from "components/source/hoc/with-flags-and-experiments";
import { get } from "actions/ce-bag-actions";
import PlansDrawerModal from "components/source/acquisition/plans_drawer/plans-drawer-modal";
import { MODAL_NAMES } from "constants/modal-names";
import { DiscoSearchServiceClient } from "clients/disco-search-service-client";
import productDrawerActions from "actions/product-drawer-actions";
import { loadThumbnailPhotos } from "components/source/universal_pdp/reviews/helpers/review-images-helper";
import ReviewModal from "../universal_pdp/reviews/review-modal";

class PDP extends React.Component {
  static propTypes = {
    acquisitionMessaging: PropTypes.shape({
      unlimited: PropTypes.object,
      unlimitedWithSku: PropTypes.object,
      update: PropTypes.object,
      updateWithSku: PropTypes.object,
    }),
    activeOrders: ordersPropType,
    analyticsCarouselContext: PropTypes.object,
    analyticsPreviousLocationContext: gridAndCarouselContextPropType,
    availabilityFetched: PropTypes.bool,
    reviewsFetched: PropTypes.bool,
    browser: browserPropType,
    ceBagCount: PropTypes.object,
    cmsContent: PDPContentPropType,
    dispatch: PropTypes.func,
    displayedModal: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    errors: PropTypes.object,
    favorites: PropTypes.array,
    featureFlags: featureFlagsPropType,
    flagsAndExperiments: featureFlagsPropType,
    fetchCmsContent: PropTypes.func.isRequired,
    fetchCEBag: PropTypes.func.isRequired,
    loadDrawerStaticProduct: PropTypes.func,
    productDrawerOpen: PropTypes.object,
    pdpUrl: PropTypes.string,
    isAccessory: PropTypes.bool,
    isBuyNow: PropTypes.bool,
    isComingSoon: PropTypes.bool,
    isRentable: PropTypes.bool,
    loading: PropTypes.bool, // some silly top-level Redux Store key that is required downstream
    membershipBag: membershipBagPropType,
    membershipState: membershipStatePropType,
    momentsWithPhotos: PropTypes.array,
    offerBackupSize: PropTypes.bool,
    product: productPropType,
    productCategory: PropTypes.shape({
      id: PropTypes.string,
    }),
    productPrice: PropTypes.object,
    productSkus: PropTypes.array,
    productFitRates: PropTypes.array,
    productCategories: PropTypes.array,
    reservation: reservationPropType,
    reviewModalIsOpen: PropTypes.bool,
    reviewsThumbnailPhotos: PropTypes.object,
    shouldLoadReviews: PropTypes.bool, // remove once on Storefront.Next
    sailthruConfig: PropTypes.object,
    shipmentPostalCode: PropTypes.string,
    userData: userDataPropType,
    workingFilters: PropTypes.object,
  };

  static defaultProps = {
    featureFlags: {},
    loading: false,
    shouldLoadReviews: true,
    userData: {},
    productCategories: [],
  };

  state = {
    isComingSoon: this.props.isComingSoon,
    isHeartEducationTooltipTriggered: false,
    isUnavailableToRent: false,
    fetchedBag: false,
    flagTestv1: true,
    flagTestv2: true,
    urgencySignalHeapFired: false,
  };

  fetchUserProductRatings() {
    if (isStorefrontNext()) return;
    const { dispatch, favorites } = this.props;

    dispatch(productRatingsActions.loadUserProductRatings());
    dispatch(shortlistsActions.loadShortlists(favorites));
  }

  initializePreviousLocationContext() {
    // GN [EXPLANATION] 11/20/20: Data Science wants to know if the user got to this PDP from a carousel or grid. If they did, we stored contextual info about that previous location in localStorage.
    // We'll read it, save it in Redux, and include it with pixels fired from this PDP when the user hearts/adds to bag. We'll also clear it from localStorage now, so it doesn't affect subsequent navigation.
    const previousLocationContext = AnalyticsPreviousLocationContextActions.readPreviousLocationContextFromStorage();
    if (previousLocationContext) {
      this.props.dispatch(AnalyticsPreviousLocationContextActions.setPreviousLocationContext(previousLocationContext));
      AnalyticsPreviousLocationContextActions.clearPreviousLocationContextFromStorage();
    }
  }

  componentDidMount() {
    const { reservation, product, userData, membershipState, workingFilters } = this.props;
    // If editing a reservation, set the rental intent to be `reserve` so the
    // `reserve` upsell drawer is open.
    if (reservation?.primaryHoldId) {
      RentalOptionIntent.setLastIntent(
        RentalOptionIntent.RentalOptions.RESERVE,
        RentalOptionIntent.ActionsTaken.EDIT_RESERVE_ATC
      );
    }

    if (this.props.featureFlags?.[Flags.PDP_CAROUSEL_RTL_REORDER]) {
      this.setState({
        isUnavailableToRent: ProductAvailability.isUnavailableToRent(
          product,
          userData,
          membershipState,
          workingFilters
        ),
      });
    }

    this.setGtmData();
    this.initializePreviousLocationContext();
    this.props.dispatch(RatingEducationTooltipsActions.getIsHeartEducationTooltipDismissed());
    this.props.fetchCmsContent();

    if (isIdentified(userData)) {
      this.props.dispatch(loadAddressBook());
    }

    // Once on Storefront.Next, ensure that userData is not loading, then only
    // call this once it has. Currently, it is called twice
    this.logMembershipUpsellEligibility(this.props);
    const userId = getUserId(this.props.userData);
    if (userId && !this.props.favorites?.length) {
      this.fetchUserProductRatings();
    }

    Converge.trackViewedProduct(this.props.product, this.props.isRentable);

    this.configureSailthru();

    loadThumbnailPhotos(this.props.product?.id, this.props.reviewsThumbnailPhotos, this.props.dispatch);

    // Note: Product data is fetched through getServerSideProps in PDP,
    // which only runs on full page loads. PDP drawer doesn't trigger it.
    // So we fetch the data on mount when it's a PDP drawer,
    // and store it in the redux state drawerStaticProduct.
    if (this.props.productDrawerOpen) {
      this.fetchStaticProduct(this.props.productDrawerOpen?.pdpUrl);
    }
  }

  configureSailthru() {
    if (this.props.sailthruConfig !== void 0) {
      // This config is picked up once the `oneTrustLoadEvent` event fires
      // see sailthru-helpers.js
      window.sailthruConfig = this.props.sailthruConfig;
    }
  }

  paintReservation(previousProps) {
    const { dispatch, reservation } = this.props;
    if (!previousProps.reservation && reservation) {
      if (!reservation.id) {
        dispatch(ReservationsActions.paintEmptyReservation(this.props));
      } else if (isStorefrontNext() && reservation.id) {
        // With a PDP served from Next, dateOptions are not included in redux.
        // When editing a reservation (i.e. clicking "edit" from the checkout UI)
        // dispatch `ReservationsActions.updateReservation()` which fetchs all the data we need.
        // This is ultimately what `paintEmptyReservation` calls above for new reservations.
        dispatch(ReservationsActions.updateReservation());
      }
    }
  }

  onMissingSkuFetchNextAvailableSku() {
    const { reservation, workingFilters, dispatch, availabilityFetched, pdpUrl } = this.props;
    // if the selected sku is unavailable
    // then attempt to update the primary sku from pdp component level
    const product = this.productHydratedWithDynamicData();
    if (!availabilityFetched) {
      return;
    } else if (
      (getSkuCount(getSelectedSku(reservation, product.skus), product) === 0 ||
        getSkuCount(getSelectedSku(reservation, product.skus), product) === void 0) &&
      workingFilters?.canonicalSizes?.length > 0
    ) {
      const availableSku = getAvailableSku(workingFilters.canonicalSizes, product);
      if (availableSku) {
        dispatch(ReservationsActions.updateReservation({ primarySku: availableSku }, pdpUrl));
      }
      // adding this condition to make primary sku is present with one size item inside reservation
      // when canonical size is not present
    } else if (
      !workingFilters?.canonicalSizes?.length &&
      product?.skus?.length === 1 &&
      product?.skus?.[0]?.id &&
      !reservation?.primarySku?.id
    ) {
      dispatch(ReservationsActions.updateReservation({ primarySku: { type: "sku", id: product.skus[0].id } }, pdpUrl));
    }
  }

  fetchPartialProps(previousProps) {
    const { userData, dispatch, workingFilters, membershipState, pdpUrl } = this.props;

    if (previousProps.userData && userData) {
      const previousLens = MembershipHelpers.getUserDataLens(previousProps.userData);
      const goToLens = MembershipHelpers.getUserDataLens(userData);
      const userJustToggledLens = previousLens !== goToLens;

      //
      // NOTE update product price due to lens being toggled
      //
      if (userJustToggledLens) {
        // When we need to retrieve updated product pricing we send this state key to the fetchPartialProps action
        const updatePricingStateKey = partialPropsStateKeys.LENS_SWAP;
        const appendString = `${updatePricingStateKey}&lens=${goToLens}`;
        dispatch(SharedActions.fetchPartialProps(appendString, () => {}, workingFilters, pdpUrl));
      } else if (MembershipUpgradesHelpers.shouldRefreshUpgradePricingCe(previousProps, this.props)) {
        // NW [EXPLANATION] 12/13/19: a subscriber did something that will have an effect on upgrade pricing.
        // because of PDP caching, we need to bust the cache with an updated state key.
        const isBagFull = MembershipHelpers.isBagFullCe(membershipState, this.props.ceBagCount);
        const subscriberUpgradeActionStateKey = isBagFull
          ? partialPropsStateKeys.SUBSCRIBER_UPGRADE_PRICING_TRUE
          : partialPropsStateKeys.SUBSCRIBER_UPGRADE_PRICING_FALSE;
        // The membership bag just became full or became not-full, so fetchPartialProps is dispatched to update product price (for upgrade feature)
        dispatch(SharedActions.fetchPartialProps(subscriberUpgradeActionStateKey, () => {}, workingFilters, pdpUrl));
      }
    }
  }

  firePageViewPixel(prevProps) {
    const { dispatch, availabilityFetched, reviewsFetched } = this.props;

    const prerequisiteState = availabilityFetched && reviewsFetched;

    if (prerequisiteState && (!prevProps.availabilityFetched || !prevProps.reviewsFetched)) {
      dispatch(PageViewActions.log(ActionTypes.PDP_PAGE_VIEW));
      dispatch(PageViewActions.pdpPageView());
    }
  }

  productHydratedWithDynamicData() {
    const { productSkus, productFitRates, productPrice, product } = this.props;

    if (productSkus && productFitRates && productPrice) {
      return { ...product, skus: productSkus, fitRates: productFitRates, price: productPrice };
    }

    return product;
  }

  didSkuAvailabilitiesUpdate(previousProps) {
    const { productSkus, userData, product, isBuyNow } = this.props;
    // Making sure initial sku fetch has already happened
    if (previousProps.availabilityFetched) {
      const prevAvailabilityType = ProductAvailability.getAvailabilityType(
        previousProps.userData,
        previousProps.product,
        previousProps.isBuyNow
      );

      const prevAvailability = ProductAvailability.skusWithNonZeroCounts(
        previousProps.productSkus,
        prevAvailabilityType
      ).map(sku => sku.size);

      const currentAvailabilityType = ProductAvailability.getAvailabilityType(userData, product, isBuyNow);
      const currentAvailability = ProductAvailability.skusWithNonZeroCounts(productSkus, currentAvailabilityType).map(
        sku => sku.size
      );

      return JSON.stringify(prevAvailability) !== JSON.stringify(currentAvailability);
    }

    return false;
  }
  // Delete the Verify LD function after use
  verifyLDExperiment_v1(availabilityFetched, userData, flagsAndExperiments, productDrawerOpen) {
    if (availabilityFetched && userData?.authDataHasLoaded && userData?.userProfileHasLoaded && this.state.flagTestv1) {
      this.setState({
        flagTestv1: false,
      });

      if (isAnonymous(userData)) {
        HeapHelpers.fireHeapEvent("ld_test_fire_heap_v1_anonymous", {
          isInV1Experiment: flagsAndExperiments?.[flagsAndExperimentNames.RSV_1372_VERIFY_LD_EXP_USING_HEAP],
          productDrawerOpen: !!productDrawerOpen,
        });
      } else if (isAuthorized(userData)) {
        HeapHelpers.fireHeapEvent("ld_test_fire_heap_v1_authorized", {
          isInV1Experiment: flagsAndExperiments?.[flagsAndExperimentNames.RSV_1372_VERIFY_LD_EXP_USING_HEAP],
          // adding authStatus because with new auth authorized users can be identified users
          authStatus: userData?.authState,
          productDrawerOpen: !!productDrawerOpen,
        });
      } else if (isIdentified(userData)) {
        HeapHelpers.fireHeapEvent("ld_test_fire_heap_v1_identified", {
          isInV1Experiment: flagsAndExperiments?.[flagsAndExperimentNames.RSV_1372_VERIFY_LD_EXP_USING_HEAP],
          authStatus: userData?.authState,
          productDrawerOpen: !!productDrawerOpen,
        });
      } else {
        HeapHelpers.fireHeapEvent("ld_test_fire_heap_v1_something_else", {
          isInV1Experiment: flagsAndExperiments?.[flagsAndExperimentNames.RSV_1372_VERIFY_LD_EXP_USING_HEAP],
          authStatus: userData?.authState,
          productDrawerOpen: !!productDrawerOpen,
        });
      }
    }
  }
  // Delete the Verify LD function after use
  verifyLDExperiment_v2(userData, flagsAndExperiments, productDrawerOpen) {
    if (
      (flagsAndExperiments?.[flagsAndExperimentNames.RSV_1372_VERIFY_LD_EXP_USING_HEAP] === true ||
        flagsAndExperiments?.[flagsAndExperimentNames.RSV_1372_VERIFY_LD_EXP_USING_HEAP] === false) &&
      userData?.authDataHasLoaded &&
      userData?.userProfileHasLoaded &&
      this.state.flagTestv2
    ) {
      this.setState({
        flagTestv2: false,
      });

      if (isAnonymous(userData)) {
        HeapHelpers.setExperimentsPropertiesInHeap(flagsAndExperiments);
        HeapHelpers.fireHeapEvent("ld_test_fire_heap_v2_anonymous", {
          isInV2Experiment: flagsAndExperiments?.[flagsAndExperimentNames.RSV_1372_VERIFY_LD_EXP_USING_HEAP],
          productDrawerOpen: !!productDrawerOpen,
        });
      } else if (isAuthorized(userData)) {
        HeapHelpers.setExperimentsPropertiesInHeap(flagsAndExperiments);
        HeapHelpers.fireHeapEvent("ld_test_fire_heap_v2_authorized", {
          isInV2Experiment: flagsAndExperiments?.[flagsAndExperimentNames.RSV_1372_VERIFY_LD_EXP_USING_HEAP],
          authStatus: userData?.authState,
          productDrawerOpen: !!productDrawerOpen,
        });
      } else if (isIdentified(userData)) {
        HeapHelpers.setExperimentsPropertiesInHeap(flagsAndExperiments);
        HeapHelpers.fireHeapEvent("ld_test_fire_heap_v2_identified", {
          isInV2Experiment: flagsAndExperiments?.[flagsAndExperimentNames.RSV_1372_VERIFY_LD_EXP_USING_HEAP],
          authStatus: userData?.authState,
          productDrawerOpen: !!productDrawerOpen,
        });
      } else {
        HeapHelpers.setExperimentsPropertiesInHeap(flagsAndExperiments);
        HeapHelpers.fireHeapEvent("ld_test_fire_heap_v2_something_else", {
          isInV2Experiment: flagsAndExperiments?.[flagsAndExperimentNames.RSV_1372_VERIFY_LD_EXP_USING_HEAP],
          authStatus: userData?.authState,
          productDrawerOpen: !!productDrawerOpen,
        });
      }
    }
  }

  handleAddressChange(previousProps) {
    const { dispatch, shipmentPostalCode, workingFilters, userData } = this.props;

    if (previousProps.shipmentPostalCode !== shipmentPostalCode) {
      dispatch(
        SharedActions.fetchPartialProps(
          `${partialPropsStateKeys.LENS_SWAP}&lens=${MembershipHelpers.getUserDataLens(userData)}`, // Use lens_swap as that gets us all the data we need
          () => {},
          { ...workingFilters, zip_code: shipmentPostalCode },
          this.props.pdpUrl
        )
      );
    }
  }

  componentDidUpdate(previousProps) {
    const { userData, fetchCEBag, isBuyNow, flagsAndExperiments, availabilityFetched, productDrawerOpen } = this.props;

    this.paintReservation(previousProps);
    this.onMissingSkuFetchNextAvailableSku();
    this.fetchPartialProps(previousProps);
    this.firePageViewPixel(previousProps);
    this.handleAddressChange(previousProps);

    if (isAnonymous(previousProps.userData) && isIdentified(userData)) {
      this.logMembershipUpsellEligibility(this.props);
    }

    if (!isIdentified(previousProps.userData) && isIdentified(userData)) {
      this.props.dispatch(loadAddressBook());
    }

    if (
      this.props.featureFlags?.[Flags.PDP_CAROUSEL_RTL_REORDER] &&
      (!previousProps.featureFlags?.[Flags.PDP_CAROUSEL_RTL_REORDER] ||
        this.props.workingFilters !== previousProps.workingFilters)
    ) {
      this.setState({
        isUnavailableToRent: ProductAvailability.isUnavailableToRent(
          this.props.product,
          this.props.userData,
          this.props.membershipState,
          this.props.workingFilters
        ),
      });
    }

    if (availabilityFetched && !previousProps.availabilityFetched) {
      this.heapLogging();
    }

    // availabity changes due to changing you lens or reserve date/duration changes
    if (this.didSkuAvailabilitiesUpdate(previousProps)) {
      this.heapLogging("availability_updated");
    }

    const currentUserId = getUserId(userData);
    const prevUserId = getUserId(previousProps.userData);
    if (currentUserId && !this.props.favorites?.length && currentUserId !== prevUserId) {
      this.fetchUserProductRatings();
    }

    // ensuring this is only called in classic lens as this need for the upsell sheet
    // treatment to work
    // Putting this behind feature flag incase the amount of calls to bag increases sharply
    if (
      flagsAndExperiments?.[flagsAndExperimentNames.RSV_UPSELL_MODAL] &&
      !isBuyNow &&
      MembershipHelpers.isClassicLens(userData) &&
      !this.state.fetchedBag &&
      isIdentified(userData)
    ) {
      this.setState({
        fetchedBag: true,
      });

      fetchCEBag();
    }

    this.urgencySignallingHeapLogging(availabilityFetched, userData, flagsAndExperiments, productDrawerOpen);
    // Delete the Verify LD function after use
    // runnign LD experiment to understand difference device context and user context and its respective attribute
    // because last time we ran these experiments they caused a lot of trouble, so trying to find a concrete way to
    // setup experiment without causing issue
    // assuming on the basis of availability fetched takes long enough to add experiment properties on heap
    this.verifyLDExperiment_v1(availabilityFetched, userData, flagsAndExperiments, productDrawerOpen);
    this.verifyLDExperiment_v2(userData, flagsAndExperiments, productDrawerOpen);
  }

  async fetchStaticProduct(url) {
    const staticProductResponse = await DiscoSearchServiceClient.getInstance().getProductsDataByPdpUrl(
      url?.split("?")[0]
    );

    this.setState({
      isComingSoon: staticProductResponse?.items?.[0]?.isComingSoon,
    });

    this.props.loadDrawerStaticProduct(staticProductResponse?.items?.[0]);
  }

  heapLogging(eventName = "pdp_loaded") {
    const { reservation, userData, workingFilters, membershipState, availabilityFetched, productDrawerOpen } =
      this.props;

    const skuHydratedProduct = this.productHydratedWithDynamicData();

    if (!availabilityFetched) return;

    const eventData = productDrawerOpen
      ? buildPDPDrawerEvents(membershipState, userData, skuHydratedProduct, workingFilters, this.state.isComingSoon)
      : buildPDPEvents(
          membershipState,
          userData,
          skuHydratedProduct,
          reservation,
          workingFilters,
          this.state.isComingSoon
        );

    HeapHelpers.addEventProperties(eventData);
    HeapHelpers.fireHeapEvent(productDrawerOpen ? "open_drawer" : eventName, eventData);
  }

  urgencySignallingHeapLogging(availabilityFetched, userData, flagsAndExperiments, productDrawerOpen) {
    if (
      (flagsAndExperiments?.[flagsAndExperimentNames.RSV_1421_URGENCY_SIGNAL] === true ||
        flagsAndExperiments?.[flagsAndExperimentNames.RSV_1421_URGENCY_SIGNAL] === false) &&
      userData?.authDataHasLoaded &&
      userData?.userProfileHasLoaded &&
      !this.state.urgencySignalHeapFired &&
      availabilityFetched
    ) {
      this.setState({
        urgencySignalHeapFired: true,
      });

      HeapHelpers.fireHeapEvent("urgency_signal_experimet", {
        urgencySignalExperimentStatus: flagsAndExperiments?.[flagsAndExperimentNames.RSV_1421_URGENCY_SIGNAL],
        authStatus: userData?.authState,
        productDrawerOpen: !!productDrawerOpen,
      });
    }
  }

  logMembershipUpsellEligibility = props => {
    const { userData, product } = props;
    return logMembershipUpsellEligibility(userData, product);
  };

  displayMembershipUpsell = () => {
    const { userData, product } = this.props;
    return displayMembershipUpsell(userData, product);
  };

  handleAddToBagFailure = () => {
    // TODO: refresh availability?
    // the commit where this block was introduced was for grid/drawer adds to
    // bag, but there are enough common components that it made sense to wire up
    // the code to handle add to bag failures here (just unclear what the action
    // should be)
  };

  handleOrientationChange = () => {
    this.props.dispatch(WindowActions.windowResize());
  };

  isAccessory() {
    // this helps in the migration to SF.Next.
    // Currently, Ruby does the work to determine whether a category isRentable
    // In the future, the SF.Next Page will simply provide the productCategory
    const {
      productCategory,
      productCategories,
      isAccessory = isAccessoryProductCategory(productCategory?.id, productCategories),
    } = this.props;

    return isAccessory;
  }

  isRentable() {
    // this helps in the migration to SF.Next.
    // Currently, Ruby does the work to determine whether a category isRentable
    // In the future, the SF.Next Page will simply provide the productCategory
    const {
      productCategory,
      productCategories,
      product,
      isRentable = isRentableProductCategory(productCategory?.id, productCategories) && !isClearance(product.price),
    } = this.props;

    return isRentable;
  }

  canOfferBackUpSize() {
    // this helps in the migration to SF.Next.
    // When serving from Ruby we can use the `offerBackupSize` prop which is injected
    // onto the page. From Next we use a port of the Ruby logic.
    const { productCategory, productCategories, product } = this.props;

    // This prop is only available when the page is served from Ruby.
    if (typeof this.props.offerBackupSize !== "undefined") {
      return this.props.offerBackupSize;
    }

    // Port of Ruby method: Storefront::PDPResult#offer_backup_size?
    return isBackupSizeCategory(productCategory?.id, productCategories) && product?.skus?.length > 0;
  }

  setGtmData = () => {
    const { product } = this.props;
    const {
      category: { id: categoryId = "" } = {},
      colors = [],
      season = "",
      embellishments = [],
      formalityScore,
    } = product || {};

    if (product) {
      GTMHelper.pushToDataLayer({
        event: "rtrPDPItemLoaded",
        content_category: categoryId,
        content_color: colors,
        content_season: season,
        content_embellishments: embellishments,
        content_formality: formalityScore,
        rtrPDPItem: product,
      });
    }
  };

  logOpenAvailability = () => {
    logReservationAction("rezo_search", this.props.reservation, this.props.product);
  };

  toggleMembershipLens = (toggleToSpecificLens = null) => {
    const { dispatch, membershipState, product, userData } = this.props;

    const isUnlimitedInventory = MembershipHelpers.hasInventoryEligibility(
      membershipState,
      inventoryEligibilities.rtr_unlimited
    );
    const isSubscriptionLens = MembershipHelpers.isSubscriptionLens(userData);
    let newLens;
    if (toggleToSpecificLens) {
      newLens = toggleToSpecificLens;
    } else if (isSubscriptionLens) {
      newLens = membershipLens.classic;
    } else if (isUnlimitedInventory) {
      newLens = membershipLens.unlimited;
    } else {
      newLens = membershipLens.RTRUpdate;
    }
    let logActionType;
    if (toggleToSpecificLens) {
      logActionType = `toggle_${toggleToSpecificLens}_lens`;
    } else if (isSubscriptionLens) {
      logActionType = "toggle_classic_lens";
    } else if (isUnlimitedInventory) {
      logActionType = "toggle_unlimited_lens";
    } else {
      logActionType = "toggle_rtrupdate_lens";
    }

    dispatch(ProductsActions.setMembershipLens(newLens, false));

    ActionLogger.inferAction({
      action: logActionType,
      object_type: isUnlimitedInventory ? "unlimited_pdp" : "rtrupdate_pdp",
      style_name: product.id,
    });
  };

  getRentalText = () => {
    const { reservation, userData } = this.props;

    // This will either come before or after the price
    if (!reservation?.date && MembershipHelpers.isClassicLens(userData)) {
      return "Rent from";
    }
    return "Reserve for";
  };

  getUpgradeAnnotationAppendString = () => {
    const { ceBagCount, membershipState, userData, featureFlags } = this.props;

    return MembershipUpgradesHelpers.getUpgradeAnnotationAppendCE(
      membershipState,
      userData,
      ceBagCount,
      featureFlags?.[Flags.UNLIMITED_ALWAYS_ON_ADD_ONS]
    );
  };

  getAnalyticsLoggingContext() {
    const { analyticsCarouselContext = {}, membershipState, userData, analyticsPreviousLocationContext } = this.props;
    const pickMode = membershipState?.shipmentAction;
    const maternityDueDate = inMaternityExperience(userData) ? getDueDate(userData) : null;

    return {
      maternity_due_date: maternityDueDate,
      pick_mode: pickMode,
      ...analyticsPreviousLocationContext,
      ...analyticsCarouselContext,
    };
  }

  logUpgradePricingTooltipClick = () => {
    const { ceBagCount, membershipState, product, userData, featureFlags } = this.props;

    if (
      MembershipUpgradesHelpers.isViewingUpgradePricing(
        membershipState,
        userData,
        ceBagCount,
        featureFlags?.[Flags.UNLIMITED_ALWAYS_ON_ADD_ONS]
      )
    ) {
      const style = product?.id;

      MembershipUpgradesHelpers.logUpgradePricingTooltipClick(style, membershipState);
    }
  };

  canRenderSplitTheBusiness = () => {
    const { membershipState, productPrice } = this.props;

    let isBulkOrClearance = false;
    const notActiveMembership = !MembershipHelpers.isActiveMembership(membershipState);

    if (productPrice) {
      isBulkOrClearance = isClearance(productPrice) || isBulk(productPrice);
    }

    return notActiveMembership && !isBulkOrClearance;
  };

  render() {
    const analyticsLoggingContext = this.getAnalyticsLoggingContext();
    return (
      <div>
        <PDPContent
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...this.props}
          availabilityFetched={this.props.availabilityFetched}
          dispatch={this.props.dispatch}
          cmsContent={this.props.cmsContent}
          analyticsLoggingContext={analyticsLoggingContext}
          displayMembershipUpsell={this.displayMembershipUpsell()}
          acquisitionMessaging={this.props.acquisitionMessaging?.update}
          onOrientationChange={this.handleOrientationChange}
          isMobileViewport={this.props.browser?.isMobileViewport}
          isAccessory={this.isAccessory()}
          isRentable={this.isRentable()}
          logOpenAvailability={this.logOpenAvailability}
          canRenderSplitTheBusiness={this.canRenderSplitTheBusiness()}
          isUnavailableToRent={this.state.isUnavailableToRent}
          handleWayfindCtaClick={this.toggleMembershipLens}
          rentalText={this.getRentalText()}
          annotationSuffix={this.getUpgradeAnnotationAppendString()}
          onClickUpgradePricingTooltip={this.logUpgradePricingTooltipClick}
          isHeartEducationTooltipTriggered={this.state.isHeartEducationTooltipTriggered}
          onAddToBagFailure={this.handleAddToBagFailure}
          offerBackupSize={this.canOfferBackUpSize()}
          product={this.productHydratedWithDynamicData()} // adding this to send hydrated sku value to the components below
        />
        {!this.props.productDrawerOpen && <UpsellBonusStyle />}
        <PlansDrawerModal isOpen={this.props.displayedModal === MODAL_NAMES.PLANS_DRAWER} />
        {this.props.momentsWithPhotos?.length > 0 && this.props.reviewModalIsOpen && (
          <ReviewModal productDisplayName={this.props.product?.displayName} productId={this.props.product?.id} />
        )}
      </div>
    );
  }
}

export { PDP as UncomposedPDP };

const mapStateToProps = state => {
  let sailthruConfig;

  if (state.publicEnv?.sailthruCustomerId && state.initialUrl && state.product?.id) {
    sailthruConfig = {
      customerId: state.publicEnv?.sailthruCustomerId,
      url: buildPdpCanonicalUrl(state.initialUrl, state.product.id),
    };
  }

  return {
    // state.availability is populated on initial render when we serve the PDP from the ruby layer,
    // it is _not_ set in the initial response from Next. Instead we need to wait until a follow-up XHR request
    // fetches availability which is triggered in the ReservationsActions.paintEmptyReservation action (triggered
    // in PDP#componentDidUpdate), at this point we explicity set `availabilityFetched: true`
    // We can use this to determine whether or not the PDP is "ready", i.e. show a shimmer effect or the ATB section.
    availabilityFetched: state.availability !== void 0 || ProductAvailability.availabilityDataFetched(state.product),
    isBuyNow: state.isBuyNow,
    ceBagCount: state.ceBagCount,
    cmsContent: state.smartComponentState?.PDPContent?.data,
    errors: state.errors,
    flagsAndExperiments: state.flagsAndExperiments,
    momentsWithPhotos: state.reviews?.reviewsWithPhotos,
    shipmentPostalCode: state.membershipState?.shipmentPostalCode,
    productDrawerOpen: state.gridProductDrawer,
    pdpUrl: state.gridProductDrawer?.pdpUrl,
    // Price, sku, and fitRates aren't included as part of the initial product fetch see [productSlug]/index.js#fetchProduct.
    // For now allow a follow-up request to hydrate the product fully in Redux and hook in here.
    productPrice: state.product?.price,
    productSkus: state.product?.skus,
    productFitRates: state.product?.fitRates,
    reservation: state.reservation,
    reviewModalIsOpen: state.pdp?.reviewModal?.isOpen,
    reviewsThumbnailPhotos: state.reviewsThumbnailPhotos,
    reviewsFetched: state.reviews !== void 0,
    sailthruConfig: sailthruConfig,
    workingFilters: state.workingFilters,
    displayedModal: state.displayedModal,
    isComingSoon: state?.staticProduct?.isComingSoon || false,
  };
};

const mapDispatchToProps = dispatch => ({
  fetchCmsContent: () => dispatch(smartComponentActions.getPDPContent()),
  fetchCEBag: () => dispatch(get()),
  loadDrawerStaticProduct: product => dispatch(productDrawerActions.loadDrawerStaticProduct(product)),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withUserData,
  withFeatureFlags(Flags.PDP_CAROUSEL_RTL_REORDER, Flags.UNLIMITED_ALWAYS_ON_ADD_ONS),
  withOnLoadFlagsAndExperiments(
    // Loading the upsell experiment here as the upsell modal will
    // be triggered from the PDP. The flag is checked in ce-bag-actions.js.
    // If there is a more appropriate place for this please move it.
    flagsAndExperimentNames.RSV_UPSELL_MODAL,
    // Loading the 8-day pricing update killswitch flag here as it will be used by the date picker
    // If there is a more appropriate place for this please move it.
    flagsAndExperimentNames.RSV_UPDATED_8_DAY_PRICING_KILLSWITCH,
    flagsAndExperimentNames.RSV_1372_VERIFY_LD_EXP_USING_HEAP,
    flagsAndExperimentNames.RSV_1421_URGENCY_SIGNAL
  ),
  withBrowser()
)(PDP);
